// Credits & Recharge — institution credits, super credits, recharge flow,
// payment-history with one-click invoice popup and PDF download.
//
// Payment gateway support:
//   • Razorpay — live when RAZORPAY_KEY_ID/SECRET are set on backend,
//                 test_mode fallback when missing. The only gateway offered.
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import api from './studentApi';
import { useStudent } from './StudentApp';
import { toast } from 'sonner';
import {
  Wallet, Sparkles, Building2, History, Check, FileText, Receipt,
  CheckCircle2, XCircle, Clock, ArrowDownToLine, CreditCard,
} from 'lucide-react';
import InvoiceModal from './InvoiceModal';

export default function StudentCreditsPage() {
  const { activeMem } = useStudent();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [invoiceOrderId, setInvoiceOrderId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [creditsData, ordersData] = await Promise.all([
        api.get('/student/credits'),
        api.get('/student/credits/orders?limit=30').catch(() => ({ orders: [] })),
      ]);
      setData(creditsData);
      setOrders(ordersData.orders || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const recharge = async (planId) => {
    setBusy(planId);
    try {
      const order = await api.post('/student/credits/super/order', { plan_id: planId, provider: 'razorpay' });

      // Test mode (no payment keys configured) → auto-verify
      if (order.test_mode) {
        const r = await api.post('/student/credits/super/verify', { order_id: order.id });
        toast.success(`+${r.credits_added} Super Credits added! (sandbox)`);
        await load();
        // Auto-pop the invoice for the freshly paid order
        setInvoiceOrderId(order.id);
        return;
      }

      if (!window.Razorpay) {
        await new Promise((res, rej) => {
          const s = document.createElement('script');
          s.src = 'https://checkout.razorpay.com/v1/checkout.js';
          s.onload = res; s.onerror = rej;
          document.body.appendChild(s);
        });
      }
      const rp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount_inr * 100,
        currency: 'INR',
        order_id: order.razorpay_order_id,
        name: 'Deskey Super Credits',
        description: `${order.credits} credits`,
        handler: async (response) => {
          try {
            const r = await api.post('/student/credits/super/verify', {
              order_id: order.id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(`+${r.credits_added} Super Credits added!`);
            await load();
            setInvoiceOrderId(order.id);
          } catch (e) { toast.error(e.message || 'Payment verify failed'); }
        },
        theme: { color: '#34A853' },
      });
      rp.open();
    } catch (e) {
      toast.error(e.message || 'Order failed');
    } finally { setBusy(null); }
  };

  if (loading) return <div className="text-sm opacity-60" data-testid="credits-loading">Loading credits…</div>;
  if (!data) return <div className="text-sm opacity-60">No credit data.</div>;

  const paidOrders = orders.filter(o => o.status === 'paid');
  const totalPaidInr = paidOrders.reduce((sum, o) => sum + (o.amount_inr || 0), 0);
  const totalCreditsBought = paidOrders.reduce((sum, o) => sum + (o.credits || 0), 0);

  return (
    <div className="space-y-10 max-w-5xl pb-20" data-testid="student-credits-page">
      <header>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Credits &amp; Billing
        </h1>
        <p className="text-sm opacity-70 mt-1">
          Each institution gives you monthly credits. Need extra? Top up Super Credits — they work across every school you join.
        </p>
      </header>

      {/* ── Balances ────────────────────────────────────────────────────── */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BalanceCard
          testid="institution-balance"
          icon={<Building2 className="w-5 h-5" />}
          eyebrow="Institution Credits"
          title={activeMem?.tenant_name || 'No active institution'}
          big={data.institution_credits.credits_remaining}
          sub={`of ${data.institution_credits.monthly_credits} monthly · billed to your school`}
        />
        <BalanceCard
          testid="super-balance"
          icon={<Sparkles className="w-5 h-5" />}
          eyebrow="Super Credits"
          title="Cross-institution"
          big={data.super_credits}
          sub="Use anywhere · paid by you · never expires"
          accent
        />
      </section>

      {/* ── Lifetime stats (only when at least one paid order exists) ─── */}
      {paidOrders.length > 0 && (
        <section className="grid grid-cols-3 gap-3" data-testid="lifetime-stats">
          <MiniStat label="Lifetime Paid"      value={`₹${totalPaidInr.toLocaleString('en-IN')}`} icon={<Receipt className="w-3.5 h-3.5" />} />
          <MiniStat label="Credits Purchased"  value={totalCreditsBought.toLocaleString('en-IN')}  icon={<Sparkles className="w-3.5 h-3.5" />} />
          <MiniStat label="Invoices Available" value={paidOrders.length}                            icon={<FileText className="w-3.5 h-3.5" />} />
        </section>
      )}

      {/* ── Recharge plans (Razorpay only) ──────────────────────────────── */}
      <section data-testid="plans-section">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <h2 className="text-xs font-bold uppercase tracking-widest opacity-60 flex items-center gap-2">
            <Wallet className="w-3.5 h-3.5" /> Top up Super Credits
          </h2>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full"
            style={{ background: 'var(--st-bg)', color: 'var(--st-mute)', border: '1px solid var(--st-border)' }}>
            <CreditCard className="w-3 h-3" /> Secured by Razorpay
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(data.plans || []).map((p, idx) => {
            const popular = idx === 1;
            return (
              <div key={p.id} className="rounded-2xl p-5 border-2 flex flex-col relative"
                style={{
                  borderColor: popular ? 'var(--st-primary)' : 'var(--st-border)',
                  background: 'var(--st-card)',
                  boxShadow: popular ? '0 0 0 3px color-mix(in srgb, var(--st-primary) 14%, transparent)' : 'none',
                }}
                data-testid={`plan-${p.id}`}>
                {popular && (
                  <div className="absolute -top-2.5 left-4 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider rounded-full text-white"
                    style={{ background: 'var(--st-primary)' }}>Most popular</div>
                )}
                <div className="text-xs font-bold uppercase tracking-wider opacity-60">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold" style={{ color: 'var(--st-primary)' }}>{p.credits}</span>
                  <span className="text-xs opacity-60">credits</span>
                </div>
                <div className="text-sm opacity-80 mt-1">
                  ₹{p.price_inr} <span className="opacity-50 text-[11px]">(${p.price_usd})</span>
                </div>
                <ul className="mt-3 space-y-1 text-xs opacity-80">
                  <li className="flex items-start gap-1.5"><Check className="w-3 h-3 mt-0.5" style={{ color: 'var(--st-primary)' }}/> Use anywhere</li>
                  <li className="flex items-start gap-1.5"><Check className="w-3 h-3 mt-0.5" style={{ color: 'var(--st-primary)' }}/> Never expires</li>
                  <li className="flex items-start gap-1.5"><Check className="w-3 h-3 mt-0.5" style={{ color: 'var(--st-primary)' }}/> GST invoice included</li>
                </ul>
                <Button onClick={() => recharge(p.id)} disabled={busy === p.id}
                  data-testid={`recharge-${p.id}`}
                  className="mt-4 rounded-full text-white font-semibold w-full" style={{ background: 'var(--st-primary)' }}>
                  {busy === p.id ? 'Processing…' : 'Recharge via Razorpay'}
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Payment History (paid Super Credit orders) ─────────────────── */}
      <section data-testid="payment-history-section">
        <h2 className="text-xs font-bold uppercase tracking-widest opacity-60 mb-3 flex items-center gap-2">
          <Receipt className="w-3.5 h-3.5" /> Payment History
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'var(--st-bg)', color: 'var(--st-mute)' }}>
            {orders.length}
          </span>
        </h2>
        {orders.length === 0 ? (
          <div className="rounded-2xl p-6 border-2 text-sm opacity-60 text-center"
            style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
            No purchases yet. Top up a Super Credit pack above to get started.
          </div>
        ) : (
          <div className="rounded-2xl border-2 overflow-hidden"
            style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
            <table className="w-full text-[13px]" data-testid="payment-history-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.12em] opacity-60 border-b"
                  style={{ borderColor: 'var(--st-border)' }}>
                  <th className="px-4 py-3 font-bold">Invoice</th>
                  <th className="px-4 py-3 font-bold">Plan</th>
                  <th className="px-4 py-3 font-bold">Date</th>
                  <th className="px-4 py-3 font-bold">Gateway</th>
                  <th className="px-4 py-3 font-bold text-right">Amount</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="px-4 py-3 font-bold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} className="border-b last:border-b-0 hover:bg-black/[0.02]"
                    style={{ borderColor: 'var(--st-border)' }}
                    data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-3 font-mono text-[11px]">{o.invoice_no}</td>
                    <td className="px-4 py-3">
                      <div className="font-bold">{o.plan_name}</div>
                      <div className="text-[11px] opacity-60">{o.credits} credits</div>
                    </td>
                    <td className="px-4 py-3 text-[11px] opacity-70">
                      {(o.paid_at || o.created_at || '').slice(0, 10)}
                    </td>
                    <td className="px-4 py-3 capitalize text-[12px]">
                      <span className="inline-flex items-center gap-1">
                        <CreditCard className="w-3 h-3 opacity-60" /> {o.provider}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold">₹{o.amount_inr}</td>
                    <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {o.status === 'paid' ? (
                        <button onClick={() => setInvoiceOrderId(o.id)}
                          data-testid={`view-invoice-${o.id}`}
                          className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border-2 hover:-translate-y-0.5 transition-all"
                          style={{ borderColor: 'var(--st-primary)', color: 'var(--st-primary)' }}>
                          <FileText className="w-3 h-3" /> Invoice
                        </button>
                      ) : (
                        <span className="text-[10px] opacity-40">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Credit-event activity log (consumption + recharges) ────────── */}
      <section data-testid="history-section">
        <h2 className="text-xs font-bold uppercase tracking-widest opacity-60 mb-3 flex items-center gap-2">
          <History className="w-3.5 h-3.5" /> Recent Activity
        </h2>
        {data.history?.length === 0 && (
          <div className="rounded-2xl p-6 border-2 text-sm opacity-60 text-center"
            style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>No activity yet.</div>
        )}
        <div className="space-y-2">
          {(data.history || []).slice(0, 20).map(h => (
            <div key={h.id} className="rounded-xl p-3 border flex items-center gap-3"
              style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
              <div className="text-xs font-mono opacity-50 w-32 truncate">{h.created_at?.slice(0, 16).replace('T', ' ')}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold capitalize">{(h.feature || h.type || '').replace(/_/g, ' ')}</div>
                <div className="text-[11px] opacity-60 truncate">{h.description}</div>
              </div>
              {h.type !== 'consumption' && h.order_id && (
                <button onClick={() => setInvoiceOrderId(h.order_id)}
                  data-testid={`activity-invoice-${h.id}`}
                  className="text-[10px] font-bold px-2 py-1 rounded-full border hover:bg-emerald-50"
                  style={{ borderColor: 'var(--st-border)', color: 'var(--st-primary)' }}>
                  <ArrowDownToLine className="w-3 h-3 inline mr-0.5" /> Receipt
                </button>
              )}
              <div className={`text-sm font-bold ${h.type === 'consumption' ? '' : 'text-green-600'}`}
                style={h.type === 'consumption' ? { color: 'var(--st-mute)' } : {}}>
                {h.type === 'consumption' ? '−' : '+'}{h.amount}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Invoice popup ──────────────────────────────────────────────── */}
      {invoiceOrderId && (
        <InvoiceModal orderId={invoiceOrderId} onClose={() => setInvoiceOrderId(null)} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Sub-components
// ════════════════════════════════════════════════════════════════════════

function StatusPill({ status }) {
  const map = {
    paid:    { label: 'Paid',    color: '#16a34a', icon: <CheckCircle2 className="w-3 h-3" /> },
    created: { label: 'Pending', color: '#ca8a04', icon: <Clock className="w-3 h-3" /> },
    failed:  { label: 'Failed',  color: '#dc2626', icon: <XCircle className="w-3 h-3" /> },
  };
  const m = map[status] || { label: status || 'Unknown', color: '#64748b', icon: <Clock className="w-3 h-3" /> };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{ color: m.color, background: `${m.color}1a`, border: `1px solid ${m.color}33` }}>
      {m.icon} {m.label}
    </span>
  );
}

function MiniStat({ label, value, icon }) {
  return (
    <div className="rounded-2xl p-4 border-2"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
      data-testid={`mini-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-60 flex items-center gap-1">{icon} {label}</div>
      <div className="text-2xl font-extrabold mt-1" style={{ color: 'var(--st-primary)' }}>{value}</div>
    </div>
  );
}

function BalanceCard({ testid, icon, eyebrow, title, big, sub, accent }) {
  return (
    <div className="rounded-3xl p-6 border-2 relative overflow-hidden" data-testid={testid}
      style={{
        borderColor: 'var(--st-border)',
        background: accent ? 'linear-gradient(135deg, var(--st-accent) 0%, var(--st-primary) 100%)' : 'var(--st-card)',
        color: accent ? '#fff' : 'inherit',
      }}>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider" style={accent ? { opacity: 0.85 } : { opacity: 0.6 }}>
        {icon} {eyebrow}
      </div>
      <div className="text-base font-bold mt-2">{title}</div>
      <div className="text-5xl font-extrabold mt-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: accent ? '#fff' : 'var(--st-primary)' }}>{big}</div>
      <div className="text-xs mt-2" style={accent ? { opacity: 0.8 } : { opacity: 0.6 }}>{sub}</div>
    </div>
  );
}
